import { z } from 'zod';
/**
 * Withholding the deposit when a client refuses to pay for a won car — owner
 * decision (a), 2026-09-28: «the WHOLE deposit is withheld as the penalty,
 * through an admin-confirmed action on the deal». Decision (b): no late-payment
 * interest, so nothing here accrues anything.
 *
 * `POST /api/admin/deals/:dealId/forfeit-deposit` does, in ONE transaction:
 *  1. reads the client's deposit (`users.biddingPower`) in the session and
 *     appends ONE `penalty` ledger row for all of it (idempotency key
 *     {@link dealForfeitProviderRef}); the balance goes to 0. A deposit of 0 is
 *     allowed: the refusal is still recorded, nothing is debited;
 *  2. cancels the deal's car invoice (`status: 'cancelled'` + `forfeit`, see
 *     `admin-finance.ts#InvoiceForfeitSchema`). The prepayment the invoice issue
 *     already took off the deposit is retained, not refunded — it is part of the
 *     withheld deposit — and so is any partial payment already received (owner,
 *     2026-09-28);
 *  3. moves the deal to the off-ladder terminal `forfeited`, with a history
 *     rung carrying the reason and a `deal.state_changed` outbox row;
 *  4. appends the {@link DealDepositForfeitedEventSchema} row — the audit record
 *     AND the client notification (`notification-events.ts`, delivered by
 *     notify-dispatch only when `NOTIFY_DISPATCH_ENABLED`).
 *
 * Behind `PAYMENTS_ENABLED`, like issuing and settling the invoice it cancels:
 * with the flag off the route answers 409 `PAYMENTS_DISABLED` and reads nothing.
 */
/**
 * The stages a refusal can be recorded in: the client owes money for the car
 * and has not claimed to have paid it. `payment_checking` is refused on its own
 * code — the client says the money is on its way, so the operator checks first.
 */
export const DEAL_FORFEITABLE_STAGES = [
    'awaiting_payment',
    'payment_overdue',
];
/** True when a deal in `stage` may have its deposit withheld. */
export function isDealForfeitable(stage) {
    return DEAL_FORFEITABLE_STAGES.includes(stage);
}
/** The ledger idempotency key of a deal's forfeit — one penalty row per deal, ever. */
export function dealForfeitProviderRef(dealId) {
    return `deal:${dealId}:forfeit`;
}
/** Longest reason an operator may give. */
export const DEAL_FORFEIT_REASON_MAX = 500;
/**
 * `POST /api/admin/deals/:dealId/forfeit-deposit` body. `confirm: true` is the
 * dialog's explicit second step — a body without it is a 400, never a forfeit.
 */
export const DealForfeitDepositBodySchema = z
    .object({
    reason: z.string().trim().min(1).max(DEAL_FORFEIT_REASON_MAX),
    confirm: z.literal(true),
})
    .strict();
/** 409 codes of the forfeit route (404 for an unknown deal, 400 for a bad body). */
export const DEAL_FORFEIT_ERROR_CODES = {
    /** `PAYMENTS_ENABLED=false` — nothing was read or written. */
    PAYMENTS_DISABLED: 'PAYMENTS_DISABLED',
    /** The deal is already `forfeited` (a replay, or a second operator). */
    ALREADY_FORFEITED: 'ALREADY_FORFEITED',
    /** The deal is not at a stage where the client owes payment for the car. */
    DEAL_NOT_FORFEITABLE: 'DEAL_NOT_FORFEITABLE',
    /** The client says they paid (`checking`) — check the payment first. */
    PAYMENT_BEING_CHECKED: 'PAYMENT_BEING_CHECKED',
    /** The invoice changed between the read and the write — reload and retry. */
    INVOICE_CHANGED: 'INVOICE_CHANGED',
};
/** 200 body of a forfeit. All money CHF. */
export const DealForfeitResultSchema = z.object({
    success: z.literal(true),
    dealId: z.string(),
    /** The deal state the refusal was recorded from. */
    from: z.string(),
    state: z.literal('forfeited'),
    invoiceNo: z.string(),
    /** Debited from `users.biddingPower` now (one `penalty` ledger row; 0 → no row). */
    depositChf: z.number().nonnegative(),
    /** Already taken off the deposit at invoice issue, retained now. */
    prepaymentChf: z.number().nonnegative(),
    /** Already paid against the invoice, retained now (no refund). */
    paidChf: z.number().nonnegative(),
    /** The whole forfeit — `depositChf + prepaymentChf + paidChf`. */
    totalChf: z.number().nonnegative(),
});
/**
 * `deal.deposit_forfeited` outbox row — who withheld how much and why, appended
 * in the SAME transaction as the ledger row, the invoice cancel and the deal
 * move. It is the audit record AND the client notification's source: the
 * dispatcher parses it with `notification-events.ts`'s
 * `DealDepositForfeitedNotificationSchema`, which keeps the amounts and the car
 * and drops `reason` and `who` — the operator's internal note never reaches the
 * client.
 */
export const DealDepositForfeitedEventSchema = z.object({
    type: z.literal('deal.deposit_forfeited'),
    data: z.object({
        dealId: z.string(),
        userId: z.string(),
        /** The won car — the notification names it. Null on a deal that records none. */
        carId: z.string().nullable(),
        invoiceNo: z.string(),
        depositChf: z.number().nonnegative(),
        prepaymentChf: z.number().nonnegative(),
        paidChf: z.number().nonnegative(),
        totalChf: z.number().nonnegative(),
        /** Null when the deposit was 0 and no ledger row was written. */
        providerRef: z.string().nullable(),
        reason: z.string(),
        /** Admin who confirmed the refusal. */
        who: z.string(),
        /** ISO timestamp. */
        when: z.string(),
    }),
});
//# sourceMappingURL=deal-forfeit.js.map